import React, {useState} from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {toast} from "react-toastify";
import {faIcons} from "../../../utils/iconConstants";
import {toastError, toastInfo, toastSuccess, toastWarning} from "../../../utils/toast";
import {convertToQueryParams} from "../../../utils/urlBuilder";
import {getUserEmail} from "../../../auth/accessTokenValidator";
import {
    currentCanonicalStoreId,
    JOB_START_FAILED,
    LINK,
    linkageErrorMessage,
    removeLinkage,
    runLink,
    saveLinkage
} from "./storeLinkage";
import cleanupLinkageWithRetry from "./cleanupLinkageWithRetry";
import CanonicalStoreSearchSelect from "./CanonicalStoreSearchSelect";

const linkedMessage = (sourceStoreId, targetCanonicalStoreId, alreadyApplied) => <>
    {alreadyApplied ? `${sourceStoreId} was already linked to` : `Linked ${sourceStoreId} to`}{' '}
    <a href={`/storeLinkage?${convertToQueryParams({canonicalStoreId: targetCanonicalStoreId})}`}
       data-testid={"linked-canonical-store-link"}>
        {targetCanonicalStoreId}
    </a>
</>;

const LinkSourceStoreModal = ({version, canonicalStoreId, member, onClose, onLinked}) => {
    const sourceStoreId = member.id;
    const [target, setTarget] = useState(null);
    const [notes, setNotes] = useState('');
    const [step, setStep] = useState(null);
    const [savedLink, setSavedLink] = useState(undefined);
    const [savedTarget, setSavedTarget] = useState(undefined);
    const [cancelling, setCancelling] = useState(false);
    const [targetLocked, setTargetLocked] = useState(false);
    const submitting = step !== null;
    const targetCanonicalStoreId = target?.value;
    const canSubmit = !!targetCanonicalStoreId && !!notes.trim();

    const onSubmit = () => {
        setStep('Saving');
        return saveLinkage(LINK, {
            version,
            canonicalStoreId: targetCanonicalStoreId,
            sourceStoreId,
            notes,
            user: getUserEmail(),
            index: savedLink?.index
        })
            .then(saved => {
                setSavedLink(saved);
                setSavedTarget(targetCanonicalStoreId);
                setStep('Linking');
                return runLink({canonicalStoreId: targetCanonicalStoreId, sourceStoreId}).then(result => {
                    setStep('Cleaning up');
                    return cleanupLinkageWithRetry(LINK, {
                        version,
                        sourceStoreId,
                        canonicalStoreId: targetCanonicalStoreId,
                        keepIndex: saved.index
                    }).then(() => result);
                });
            })
            .then(result => {
                const toastLinked = result?.alreadyApplied ? toastInfo : toastSuccess;
                toastLinked(toast, linkedMessage(sourceStoreId, targetCanonicalStoreId, result?.alreadyApplied), {autoClose: false});
                onLinked();
            })
            .catch(linkError => {
                toastError(toast, `Failed to link ${sourceStoreId} to ${targetCanonicalStoreId}: ${linkageErrorMessage(linkError)}`);
                if (linkError.code === JOB_START_FAILED) setTargetLocked(true);
                setStep(null);
            });
    };

    const removeLinkAndClose = () => {
        setStep('Removing');
        return removeLinkage(LINK, {version, record: savedLink}).then(() => onClose());
    };

    const keepLinkAndCleanUp = () => {
        toastInfo(toast, `${sourceStoreId} is now linked to ${savedTarget}, so the link record was kept`);
        setStep('Cleaning up');
        return cleanupLinkageWithRetry(LINK, {version, sourceStoreId, canonicalStoreId: savedTarget, keepIndex: savedLink.index})
            .then(() => onLinked());
    };

    const keepLinkWithoutCleanUp = (current) => {
        toastWarning(toast, `${sourceStoreId} is now in ${current}, not ${savedTarget}; the link record was kept without cleanup`);
        onLinked();
    };

    const cancelForCurrentStore = (current) => {
        if (current === canonicalStoreId) return removeLinkAndClose();
        if (current === savedTarget) return keepLinkAndCleanUp();
        return keepLinkWithoutCleanUp(current);
    };

    const checkGraphThenCancel = () => {
        setCancelling(true);
        setStep('Checking');
        return currentCanonicalStoreId({version, source: member.source, sourceStoreId: member.sourceStoreId})
            .then(cancelForCurrentStore);
    };

    const onCancel = () => {
        if (!savedLink) {
            onClose();
            return;
        }
        return checkGraphThenCancel().catch(cancelError => {
            toastError(toast, `Failed to cancel the link of ${sourceStoreId}: ${linkageErrorMessage(cancelError)}`);
            setCancelling(false);
            setStep(null);
        });
    };

    const stepInProgress = <><i className={`${faIcons.spinner} fa-spin`} data-testid={"spinner"}/><span>{step}</span></>;

    return (
        <Modal
            isOpen={true}
            className="Modal link-source-store-modal"
            overlayClassName="Overlay"
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={() => !submitting && onCancel()}
            testId={"link-source-store-modal"}
        >
            <h5 className={"modal-header-text"}>Link Source Store to Another Canonical Store</h5>
            <div className={"store-info-field"}>
                <div className={"store-info-field-label"}>Current Canonical Store</div>
                <div className={"store-info-field-value"} data-testid={"link-canonical-store-id"}>{canonicalStoreId}</div>
            </div>
            <div className={"store-info-field"}>
                <div className={"store-info-field-label"}>Source Store</div>
                <div className={"store-info-field-value"} data-testid={"link-source-store-id"}>{sourceStoreId}</div>
            </div>
            <div className={"store-info-field"}>
                <label className={"store-info-field-label"} htmlFor={"link-target-search"}>Target Canonical Store</label>
                <CanonicalStoreSearchSelect
                    inputId={"link-target-search"}
                    className={"link-target-select"}
                    version={version}
                    excludeCanonicalStoreId={canonicalStoreId}
                    value={target}
                    onChange={setTarget}
                    isDisabled={submitting || targetLocked}
                />
            </div>
            <div className={"store-info-field"}>
                <div className={"store-info-field-label"}>Notes</div>
                <textarea
                    title={'Link Notes'}
                    placeholder={'What reason do you want to link?'}
                    className={"modal-alerts-text-area-wide"}
                    value={notes}
                    disabled={submitting}
                    onChange={e => setNotes(e.target.value)}
                />
            </div>
            <div className={"right-things"}>
                <button type="button" className="button modal-button cancel" name="cancel"
                        onClick={onCancel} disabled={submitting}>
                    {cancelling ? stepInProgress : "Cancel"}
                </button>
                <button type="button" className={"button modal-button confirm"} name="submit"
                        onClick={onSubmit} disabled={submitting || !canSubmit}>
                    {submitting && !cancelling ? stepInProgress : "Submit"}
                </button>
            </div>
        </Modal>
    );
};

LinkSourceStoreModal.propTypes = {
    version: PropTypes.string.isRequired,
    canonicalStoreId: PropTypes.string.isRequired,
    member: PropTypes.shape({
        id: PropTypes.string.isRequired,
        source: PropTypes.string.isRequired,
        sourceStoreId: PropTypes.string.isRequired,
    }).isRequired,
    onClose: PropTypes.func.isRequired,
    onLinked: PropTypes.func.isRequired,
};

export default LinkSourceStoreModal;
